import {
  BaseComboboxInputProps,
  ComboboxInput,
} from "@/lib/Components/Form/Inputs/ComboboxInput";
import {
  InvoiceListItem,
  invoiceListQuery,
} from "@/app/Invoices/GraphQL/invoiceListQuery";
import { ResultOf, VariablesOf } from "@graphql-typed-document-node/core";
import { invoiceRecordQuery } from "@/app/Invoices/GraphQL/invoiceRecordQuery";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { InvoiceStatus } from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Highlighter } from "@/lib/Components/Highlighter";
import { invoiceMessages } from "@/app/Invoices/messages/invoiceMessages";

const invoiceStatusColorMap: Record<InvoiceStatus, BadgeColor> = {
  [InvoiceStatus.Draft]: "zinc",
  [InvoiceStatus.Issued]: "yellow",
  [InvoiceStatus.Paid]: "green",
  [InvoiceStatus.Voided]: "zinc",
  [InvoiceStatus.Overdue]: "red",
};

export function InvoiceInput({
  name,
  label,
  supplierId,
  ...props
}: BaseComboboxInputProps<
  ResultOf<typeof invoiceListQuery>,
  VariablesOf<typeof invoiceListQuery>,
  InvoiceListItem,
  ResultOf<typeof invoiceRecordQuery>,
  VariablesOf<typeof invoiceRecordQuery>
> & {
  supplierId?: string;
}) {
  return (
    <ComboboxInput
      document={invoiceListQuery}
      accessor={(data) => data.invoices.data}
      recordDocument={invoiceRecordQuery}
      recordAccessor={(data) => data.invoice}
      getQueryVariables={(search) => ({
        first: 20,
        page: 1,
        search,
        supplierId,
        status: [
          InvoiceStatus.Draft,
          InvoiceStatus.Issued,
          InvoiceStatus.Paid,
          InvoiceStatus.Overdue,
        ],
      })}
      getKey={(item) => item.id}
      getInputNode={(item) => item.reference}
      getListItemNode={(item, search) => <Node item={item} search={search} />}
      name={name}
      label={label}
      getViewNode={(item) => (item ? item.reference : "--")}
      LeadingIcon={DocumentTextIcon}
      {...props}
    />
  );
}

function Node({ item, search }: { item: InvoiceListItem; search: string }) {
  return (
    <div className="flex w-full flex-col gap-0.5">
      <Highlighter searchString={search}>{item.reference}</Highlighter>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-500">
          {item.payee.name} &middot;{" "}
          {formatCurrency(item.amount_inc_tax, item.currency)}
        </span>
        <Badge
          color={invoiceStatusColorMap[item.status]}
          className="ml-auto shrink-0 text-[10px]/4"
        >
          {invoiceMessages.status[item.status]}
        </Badge>
      </div>
    </div>
  );
}
